/**
 * Connect navigation analytics - fire-and-forget beacon (Derick 2026-09-01).
 * Never throws, never blocks, never breaks the page. phoneKey is an analytics
 * label read from the stored session - identity still lives with OTP.
 *
 * NEXUS_REST_BASE: the dev server proxies '/nexus-rest' same-origin (so a
 * phone over the tunnel works); prod sets VITE_NEXUS_REST to the API origin -
 * the static site would otherwise swallow the path with an SPA-fallback 200
 * (the bug that silently broke resume upload on prod, found 2026-09-01).
 *
 * Behavior analytics (2026-09-02): source attribution (?s= on any /connect*
 * landing, first value wins, carried on the funnel events), per-session
 * counters (first visit, tab switches, swipes) and a `session_end` beacon on
 * pagehide / hidden. All additive - every call site is still `track(type, meta)`.
 */
export const NEXUS_REST_BASE: string =
  ((import.meta as any).env?.VITE_NEXUS_REST as string) || '/nexus-rest';

const SID_KEY = 'cx-track-sid';
const SRC_KEY = 'cx-src';
const EVENT_KEY = 'cx-event';
const SESS_KEY = 'cx-track-sess';
/** Events that carry the acquisition source in meta. */
const SRC_TYPES = new Set([
  'page_visit',
  'opp_public_tap',
  'opp_invite_land',
  'login_verified',
  'opp_swipe',
  'meet_swipe',
  'opp_qual_submit'
]);
const SWIPE_TYPES = new Set(['meet_swipe', 'opp_swipe']);
const SESSION_END_MIN_GAP_MS = 60_000;

const sid = (): string => {
  try {
    let s = localStorage.getItem(SID_KEY);
    if (!s) {
      s = Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem(SID_KEY, s);
    }
    return s;
  } catch {
    return 'anon';
  }
};

const phoneKey = (): string => {
  try {
    const raw = localStorage.getItem('pc-connect-home');
    if (raw) return String(JSON.parse(raw)?.phone || '');
  } catch {
    /* noop */
  }
  return '';
};

/** True while a super-admin is simulating another person (the room's "view as").
 *  Those beacons carry the SIMULATED person's phoneKey, so without a label they
 *  are indistinguishable from that person opening the app themselves - which is
 *  exactly how an admin walk-through can fake campaign activity. */
const simulating = (): boolean => {
  try {
    return !!localStorage.getItem('cx-sim');
  } catch {
    return false;
  }
};

const payload = (type: string, meta?: Record<string, string>): string =>
  JSON.stringify({
    sessionId: sid(),
    phoneKey: phoneKey(),
    type,
    path: window.location.pathname,
    meta: simulating() ? { ...(meta || {}), sim: '1' } : meta || {},
    ua: (navigator.userAgent || '').slice(0, 180)
  });
/** Plain fire-and-forget POST (no session bookkeeping) - what track() ends in. */
const post = (type: string, meta?: Record<string, string>): void => {
  try {
    fetch(`${NEXUS_REST_BASE}/connect/track`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: payload(type, meta),
      keepalive: true
    }).catch(() => {
      /* beacon */
    });
  } catch {
    /* noop */
  }
};

/** Persist ?s=<source> from any /connect* landing. Never overwrites - the
 *  first touch is the attribution, a later campaign link does not steal it. */
const captureSrc = (): void => {
  try {
    if (!window.location.pathname.startsWith('/connect')) return;
    const s = String(new URLSearchParams(window.location.search).get('s') || '')
      .trim()
      .slice(0, 64);
    if (!s) return;
    if (!localStorage.getItem(SRC_KEY)) localStorage.setItem(SRC_KEY, s);
  } catch {
    /* noop */
  }
};
/* THE CAMPAIGN EVENT, KEPT. `?event=<slug>` is the only campaign parameter that
   was never banked: `featuredSlug` is a computed off the live query and
   `pendingEventSlug` is an in-memory ref, so both die on a reload - and a link
   forwarded without its query string ("check this out: pilotcity.com/connect")
   silently drops the event the sender meant. `?s=` and `?i=` have survived that
   for months by the three lines below; this is the same trick, not new
   machinery.

   LAST touch wins, unlike `?s=`. A source is an attribution and belongs to
   whoever got there first; the event is the thing the person is currently
   trying to register for, and the most recent link they followed is the best
   evidence of which one that is. */
const captureEvent = (): void => {
  try {
    if (!window.location.pathname.startsWith('/connect')) return;
    const e = String(new URLSearchParams(window.location.search).get('event') || '')
      .trim()
      .toLowerCase()
      .slice(0, 64);
    if (!e || !/^[a-z0-9-]+$/.test(e)) return;
    localStorage.setItem(EVENT_KEY, e);
  } catch {
    /* noop */
  }
};
/** The event this device last arrived for, '' when it never did. */
export const trackedEvent = (): string => {
  try {
    return localStorage.getItem(EVENT_KEY) || '';
  } catch {
    return '';
  }
};
/** Cleared once the person is registered, so a stale slug cannot outlive its
 *  event and feature a gathering that has already happened. */
export const clearTrackedEvent = (): void => {
  try {
    localStorage.removeItem(EVENT_KEY);
  } catch {
    /* noop */
  }
};
export const trackSrc = (): string => {
  try {
    return localStorage.getItem(SRC_KEY) || '';
  } catch {
    return '';
  }
};

/* Member-minted invites (PR 2): the device keeps an ORDERED list of the
   invite tokens it has seen (`?i=` on any /connect* landing). Attribution is
   LAST TOUCH and transparent - the newest entry is the default credit at join
   and the invitee can pick when 2+ inviters are on the list. `first` is
   filled once the token resolves as a referral (rememberInviteFirst). */
const INV_KEY = 'cx-inv';
const INV_SEEN_KEY = 'cx-inv-seen';
const INV_MAX = 5;
const INV_SEEN_MAX = 30;
export interface InviteSeen {
  token: string;
  /** Sharer's first name - set only for member-minted referral tokens. */
  first: string;
  at: number;
}
const readJson = <T>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const p = JSON.parse(raw);
    return (p as T) ?? fallback;
  } catch {
    return fallback;
  }
};
const writeJson = (key: string, v: unknown): void => {
  try {
    localStorage.setItem(key, JSON.stringify(v));
  } catch {
    /* noop */
  }
};
/** Oldest first, newest LAST. Never throws. */
export const inviteList = (): InviteSeen[] => {
  const arr = readJson<unknown>(INV_KEY, []);
  if (!Array.isArray(arr)) return [];
  return arr
    .filter((e: any) => e && typeof e.token === 'string' && e.token)
    .map((e: any) => ({
      token: String(e.token),
      first: typeof e.first === 'string' ? e.first : '',
      at: Number(e.at) || 0
    }));
};
/** The most recent invite the device saw (last touch), or null. */
export const latestInvite = (): InviteSeen | null => {
  const l = inviteList();
  return l.length ? l[l.length - 1] : null;
};
/** Fill in the sharer's first name once the token resolves as a referral. */
export const rememberInviteFirst = (token: string, first: string): void => {
  const t = String(token || '').trim();
  if (!t) return;
  const l = inviteList();
  const i = l.findIndex(e => e.token === t);
  if (i < 0) return;
  l[i] = {
    ...l[i],
    first: String(first || '')
      .trim()
      .slice(0, 40)
  };
  writeJson(INV_KEY, l);
};
/** Record `?i=<token>` from a /connect* landing: dedupe by token (a re-seen
 *  token moves to the END - it is the newest touch), cap at INV_MAX, and emit
 *  `invite_open` ONCE per token (a seen set in the same storage). */
export const captureInvite = (): void => {
  try {
    if (!window.location.pathname.startsWith('/connect')) return;
    const t = String(new URLSearchParams(window.location.search).get('i') || '')
      .trim()
      .slice(0, 64);
    if (!t) return;
    const l = inviteList().filter(e => e.token !== t);
    const prev = inviteList().find(e => e.token === t);
    l.push({ token: t, first: prev?.first || '', at: Date.now() });
    writeJson(INV_KEY, l.slice(-INV_MAX));
    const seenRaw = readJson<unknown>(INV_SEEN_KEY, []);
    const seen = Array.isArray(seenRaw) ? seenRaw.map(String) : [];
    if (seen.includes(t)) return;
    seen.push(t);
    writeJson(INV_SEEN_KEY, seen.slice(-INV_SEEN_MAX));
    post('invite_open', { ref: t });
  } catch {
    /* noop */
  }
};

/* Session counters: survive a reload within the tab (sessionStorage), fall
   back to memory when storage is unavailable. secs counts from the first
   page_visit of the tab; tabs / swipes accumulate across the Connect pages. */
type Sess = { t0: number; tabs: number; swipes: number };
let memSess: Sess = { t0: 0, tabs: 0, swipes: 0 };
const readSess = (): Sess => {
  try {
    const raw = sessionStorage.getItem(SESS_KEY);
    if (raw) {
      const p = JSON.parse(raw);
      if (p && typeof p.t0 === 'number')
        return {
          t0: p.t0,
          tabs: Math.trunc(Number(p.tabs) || 0),
          swipes: Math.trunc(Number(p.swipes) || 0)
        };
    }
  } catch {
    /* noop */
  }
  return memSess;
};
const writeSess = (s: Sess): void => {
  memSess = s;
  try {
    sessionStorage.setItem(SESS_KEY, JSON.stringify(s));
  } catch {
    /* noop */
  }
};
const bumpSess = (type: string): void => {
  const s = readSess();
  if (type === 'page_visit' && !s.t0) s.t0 = Date.now();
  if (type === 'tab_switch') s.tabs += 1;
  if (SWIPE_TYPES.has(type)) s.swipes += 1;
  writeSess(s);
};

/** Unload-safe send: sendBeacon first (survives pagehide), fetch keepalive as
 *  the fallback where sendBeacon is missing or refuses the payload. */
const sendBeacon = (body: string): void => {
  try {
    if (typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([body], { type: 'application/json' });
      if (navigator.sendBeacon(`${NEXUS_REST_BASE}/connect/track`, blob)) return;
    }
  } catch {
    /* fall through */
  }
  try {
    fetch(`${NEXUS_REST_BASE}/connect/track`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true
    }).catch(() => {
      /* beacon */
    });
  } catch {
    /* noop */
  }
};

let lastSessionEndAt = 0;
const sendSessionEnd = (): void => {
  try {
    const now = Date.now();
    if (now - lastSessionEndAt < SESSION_END_MIN_GAP_MS) return;
    const s = readSess();
    if (!s.t0) return; // no page_visit yet - nothing to summarize
    lastSessionEndAt = now;
    sendBeacon(
      payload('session_end', {
        secs: String(Math.round((now - s.t0) / 1000)),
        tabs: String(s.tabs),
        swipes: String(s.swipes)
      })
    );
  } catch {
    /* noop */
  }
};
let sessionEndInstalled = false;
const installSessionEnd = (): void => {
  if (sessionEndInstalled) return;
  sessionEndInstalled = true;
  try {
    window.addEventListener('pagehide', sendSessionEnd);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') sendSessionEnd();
    });
  } catch {
    /* noop */
  }
};

export const track = (type: string, meta?: Record<string, string>): void => {
  try {
    captureSrc();
    captureInvite();
    captureEvent();
    installSessionEnd();
    bumpSess(type);
    const m: Record<string, string> = { ...(meta || {}) };
    if (SRC_TYPES.has(type)) m.src = trackSrc();
    post(type, m);
  } catch {
    /* beacons never break the page */
  }
};

/* Impressions (story A0, Sep-26 push): a surface that RENDERS is worth one
   beacon per page load, not one per re-render. Keyed on type + the meta
   values, in memory only - a reload is a new impression by design. */
const onceSeen = new Set<string>();
export const trackOnce = (type: string, meta?: Record<string, string>): void => {
  try {
    const m = meta || {};
    const key = `${type}|${Object.keys(m)
      .sort()
      .map(k => `${k}=${m[k]}`)
      .join('&')}`;
    if (onceSeen.has(key)) return;
    onceSeen.add(key);
    track(type, meta);
  } catch {
    /* beacons never break the page */
  }
};
